import React, { useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTenantInfo, listProducts, createProduct, updateProduct, uploadProductImage, deleteProductImage } from '../api/products'
import { formatApiError } from '../lib/apiErrorMessage'
import { useLangPrefix } from '../hooks/useLangPrefix'
import DesktopShell from '../components/layout/DesktopShell'
import AppShell from '../components/layout/AppShell'
import useManagerStore from '../store/managerStore'
import { canAccess, FULL_ACCESS, normalizeRole, RBAC_MODULES } from '../lib/rbac'
import { AMOUNT_TOO_LARGE_ERROR, UNSUPPORTED_CURRENCY_ERROR, formatMoney, getMaxAmount, getMinorUnits, parseMoneyInput } from '../lib/money'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'
import { formatAmountForInput } from '../lib/moneyInput'

const INDUSTRY_CATEGORIES = {
    fnb: ['starter','main_course','dessert','beverage','alcohol','combo','set_menu','takeaway'],
    spa: ['massage','facial','body_treatment','nail','hair','package','membership','add_on'],
    hotel: ['room_standard','room_deluxe','room_suite','breakfast','airport_transfer','laundry','spa_access','dining_credit','early_checkin'],
    travel: ['day_tour','multi_day_tour','transfer','activity','visa_service','travel_insurance','accommodation_upgrade','guide_service'],
    education: ['course_full','course_module','trial_class','private_lesson','group_class','material_pack','exam_prep','membership_monthly'],
}
const inputStyle = { width: '100%', padding: '8px 10px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'white' }
const labelStyle = { fontSize: 12, color: '#555', marginBottom: 4, display: 'block', fontWeight: 500 }
const sectionStyle = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 20, marginBottom: 16 }
const sectionTitle = { fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }
const MAX_STOCK_QTY = 1000000000
const MAX_PRODUCT_IMAGES = 3
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024
const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const OFFERING_TYPE_BY_INDUSTRY = {
    fnb: 'dish',
    hotel: 'room',
    spa: 'service',
    travel: 'tour',
    education: 'course',
}
const PRICING_UNIT_BY_INDUSTRY = {
    fnb: 'dish',
    hotel: 'night',
    spa: 'session',
    travel: 'package',
    education: 'course',
}

function sanitizeDigits(value) {
    return value.replace(/[^\d]/g, '')
}

// Price inputs keep the raw typed string; parseMoneyInput validates it on blur
// and submit (SPEC-growchat-tenant-currency.md, Design 8). Amounts stay
// decimal numbers in major units: never convert with `* 100` cents math.

const LONE_SEPARATOR_TWO_DECIMALS = /^(\d+)([.,])\d{2}$/
const GROUPED_THEN_TWO_DECIMALS = /^[1-9]\d{0,2}([.,])\d{3}(?:\1\d{3})*([.,])\d{2}$/
const POSSIBLE_FIRST_GROUP = /^[1-9]\d{0,2}$/

// True when the text can only be read as an amount with 2 decimals. `1,25`
// is NOT: it may still become `1,250,000` (grouping), so typing continues and
// blur/submit validation reports any ambiguity.
function isUnambiguousTwoDecimalState(text) {
    const grouped = GROUPED_THEN_TWO_DECIMALS.exec(text)
    if (grouped) return grouped[1] !== grouped[2]
    const lone = LONE_SEPARATOR_TWO_DECIMALS.exec(text)
    if (!lone) return false
    return lone[2] === '.' || !POSSIBLE_FIRST_GROUP.test(lone[1])
}

/** Ignore a 3rd decimal digit typed at the end of an unambiguous 2-decimal value. */
function isBlockedDecimalKeystroke(prev, next, currency) {
    if (getMinorUnits(currency) !== 2) return false
    if (next.length !== prev.length + 1 || !next.startsWith(prev)) return false
    return /\d$/.test(next) && isUnambiguousTwoDecimalState(prev.trim())
}

/** An insert of more than one character (paste, autofill) is validated at once. */
function isBulkInsert(prev, next) {
    return next.length > prev.length + 1
}

/** A stored amount with more decimals than the currency allows (e.g. 4.4999999 CAD). */
function hasExtraPrecision(value, currency) {
    const parsed = Number(value)
    return Number.isFinite(parsed) && Number(parsed.toFixed(getMinorUnits(currency))) !== parsed
}

/** Loaded amount as sent back when untouched, or `fallback` when absent/invalid. */
function loadedAmountOr(value, fallback) {
    const parsed = value === null || value === undefined || value === '' ? NaN : Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
}

function isValidTimeValue(value) {
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value)
}

function timeToMinutes(value) {
    const [hours, minutes] = value.split(':').map(Number)
    return (hours * 60) + minutes
}

function parseCsv(value) {
    return (value || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
}

function resolveLoadedSellingPrice(product) {
    const candidates = [
        product?.final_display_price,
        product?.pricing?.sale_price,
        product?.pricing?.base_price,
        product?.price,
    ]
    for (const candidate of candidates) {
        const parsed = Number(candidate)
        if (Number.isFinite(parsed) && parsed >= 0) return parsed
    }
    return null
}

function normalizeLoadedImages(product) {
    const urls = []
    const pushIfValid = (value) => {
        if (typeof value === 'string' && value.trim()) urls.push(value.trim())
    }
    if (Array.isArray(product?.images)) {
        product.images.forEach((image) => {
            if (typeof image === 'string') {
                pushIfValid(image)
                return
            }
            if (image && typeof image === 'object') pushIfValid(image.url)
        })
    }
    pushIfValid(product?.media?.primary_image_url)
    if (Array.isArray(product?.media?.gallery_image_urls)) product.media.gallery_image_urls.forEach(pushIfValid)
    pushIfValid(product?.image_url)
    return Array.from(new Set(urls)).map(url => ({ url, source: 'upload' }))
}

export default function ProductFormPage() {
    const { t } = useTranslation()
    const { productId } = useParams()
    const navigate = useNavigate()
    const { prefix, lang } = useLangPrefix()
    const tenantId = useManagerStore((s) => s.tenantId)
    const isEdit = !!productId
    const isDesktop = window.innerWidth >= 1024
    const storeRole = useManagerStore((s) => s.role)
    const role = normalizeRole(storeRole)
    const canManageProducts = canAccess(role, RBAC_MODULES.products, FULL_ACCESS)
    // Prices are gated on a resolved tenant currency: the VND placeholder used
    // while loading must not let a CAD tenant type a VND-shaped price.
    const { currency: tenantCurrency, isResolved: currencyResolved, isError: currencyError } = useTenantCurrencyState()

    const [tenantIndustry, setTenantIndustry] = useState('fnb')
    const [label, setLabel] = useState('')
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [shortDesc, setShortDesc] = useState('')
    const [detailedDesc, setDetailedDesc] = useState('')
    const [offeringType, setOfferingType] = useState('')
    const [category, setCategory] = useState('')
    const [catSearch, setCatSearch] = useState('')
    const [showCatDrop, setShowCatDrop] = useState(false)
    const [highlight, setHighlight] = useState('')
    const [benefits, setBenefits] = useState('')
    const [skuCode, setSkuCode] = useState('')
    const [tags, setTags] = useState('')
    const [targetAudience, setTargetAudience] = useState('')
    const [experienceLevel, setExperienceLevel] = useState('beginner')
    const [goal, setGoal] = useState('')
    const [duration, setDuration] = useState('')
    const [durationUnit, setDurationUnit] = useState('')
    const [sessionCount, setSessionCount] = useState('')
    const [scheduleType, setScheduleType] = useState('fixed')
    const [capacity, setCapacity] = useState('')
    const [size, setSize] = useState('')
    const [location, setLocation] = useState('')
    const [format, setFormat] = useState('online')
    // Loaded numeric prices; the input text is derived once the currency is known.
    const [loadedPrices, setLoadedPrices] = useState({ base: null, sale: null, original: null })
    // Raw typed text of the selling price; null means "not edited yet".
    const [salePriceText, setSalePriceText] = useState(null)
    const [salePriceError, setSalePriceError] = useState(null)
    const [pricingUnit, setPricingUnit] = useState('')
    const [promotionContent, setPromotionContent] = useState('')
    const [stockQty, setStockQty] = useState('0')
    const [specifications, setSpecifications] = useState('')
    const [link, setLink] = useState('')
    const [videoUrl, setVideoUrl] = useState('')
    const [active, setActive] = useState(true)
    const [availableFrom, setAvailableFrom] = useState('')
    const [availableTo, setAvailableTo] = useState('')
    const [maxQty, setMaxQty] = useState('')
    const [keywords, setKeywords] = useState('')
    const [attributesInput, setAttributesInput] = useState('{}')
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [images, setImages] = useState([])
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(isEdit)
    const catRef = useRef(null)
    const fileRef = useRef(null)

    useEffect(() => {
        if (!tenantId) {
            setTenantIndustry('fnb')
            return
        }
        getTenantInfo(tenantId).then(d => setTenantIndustry(d.industry_type || 'fnb')).catch(() => {})
    }, [tenantId])

    useEffect(() => {
        const defaultOfferingType = OFFERING_TYPE_BY_INDUSTRY[tenantIndustry] || 'product'
        const defaultPricingUnit = PRICING_UNIT_BY_INDUSTRY[tenantIndustry] || 'item'
        setOfferingType(prev => prev || defaultOfferingType)
        setPricingUnit(prev => prev || defaultPricingUnit)
    }, [tenantIndustry])

    useEffect(() => {
        if (!isEdit) return
        if (!tenantId) {
            setLoading(false)
            return
        }
        listProducts(tenantId, {}).then(data => {
            const p = (data.products || []).find(x => x.id === productId)
            if (!p) return
            const loadedPrice = resolveLoadedSellingPrice(p)
            const parsedAttributes = (p.attributes && typeof p.attributes === 'object' && !Array.isArray(p.attributes)) ? p.attributes : {}
            const fallbackOfferingType = OFFERING_TYPE_BY_INDUSTRY[tenantIndustry] || 'product'
            const fallbackPricingUnit = PRICING_UNIT_BY_INDUSTRY[tenantIndustry] || 'item'
            const loadedBasePrice = p?.pricing?.base_price ?? loadedPrice ?? null
            const loadedSalePrice = p?.pricing?.sale_price ?? loadedPrice ?? null
            const loadedOriginalPrice = p?.pricing?.original_price ?? loadedPrice ?? null
            const safeCategory = p.category || ''
            setName(p.name || '')
            setLabel(p.label || '')
            setDescription(p.description || '')
            setShortDesc(p.short_description || '')
            setDetailedDesc(p.detailed_description || '')
            setOfferingType(p.offering_type || fallbackOfferingType)
            setCategory(safeCategory)
            setCatSearch(safeCategory)
            setHighlight(p.highlight || '')
            setBenefits(Array.isArray(p.benefits) ? p.benefits.join(', ') : '')
            setSkuCode(p.sku_code || p.sku || '')
            setTags(Array.isArray(p.tags) ? p.tags.join(', ') : '')
            setTargetAudience(Array.isArray(p.target_audience) ? p.target_audience.join(', ') : '')
            setExperienceLevel(p.experience_level || 'beginner')
            setGoal(p.goal || '')
            setDuration(p.duration === null || p.duration === undefined ? '' : String(p.duration))
            setDurationUnit(p.duration_unit || '')
            setSessionCount(p.session_count === null || p.session_count === undefined ? '' : String(p.session_count))
            setScheduleType(p.schedule_type || 'fixed')
            setCapacity(p.capacity === null || p.capacity === undefined ? '' : String(p.capacity))
            setSize(p.size || '')
            setLocation(p.location || '')
            setFormat(p.format || 'online')
            setLoadedPrices({ base: loadedBasePrice, sale: loadedSalePrice, original: loadedOriginalPrice })
            setSalePriceText(null)
            setSalePriceError(null)
            setPricingUnit(p?.pricing?.unit || fallbackPricingUnit)
            setPromotionContent(p.promotion_content || '')
            setStockQty(String(p.stock_quantity ?? 0))
            setSpecifications(p.specifications || '')
            setLink(p.link || '')
            setVideoUrl(p?.media?.video_url || '')
            setActive(p.active ?? true)
            setAvailableFrom(p.available_from || '')
            setAvailableTo(p.available_to || '')
            setMaxQty(p.max_quantity ? String(p.max_quantity) : '')
            setKeywords(Array.isArray(p.keywords) ? p.keywords.join(', ') : '')
            setAttributesInput(JSON.stringify(parsedAttributes, null, 2))
            setImages(normalizeLoadedImages(p))
            setLoading(false)
        }).catch(() => setLoading(false))
    }, [productId, tenantId, isEdit, tenantIndustry])

    useEffect(() => {
        function handleClick(e) {
            if (catRef.current && !catRef.current.contains(e.target)) setShowCatDrop(false)
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const predefined = INDUSTRY_CATEGORIES[tenantIndustry] || []
    const filteredCats = [
        ...predefined.filter(c => !catSearch || c.includes(catSearch.toLowerCase())),
        ...(catSearch && !predefined.includes(catSearch) ? [`${catSearch} ${t('productForm.categoryNew')}`] : [])
    ]
    // A loaded price is never rounded for display: extra decimals are shown as
    // stored, with a hint, and sent back unchanged unless the user edits them.
    const saleHasExtraPrecision = salePriceText === null && currencyResolved && hasExtraPrecision(loadedPrices.sale, tenantCurrency)
    const loadedSaleText = saleHasExtraPrecision ? String(Number(loadedPrices.sale)) : formatAmountForInput(loadedPrices.sale, tenantCurrency)
    const salePrice = salePriceText ?? (currencyResolved ? loadedSaleText : '')
    const hasDecimals = getMinorUnits(tenantCurrency) > 0

    function priceErrorMessage(errorCode) {
        if (errorCode === AMOUNT_TOO_LARGE_ERROR) {
            return t('productForm.priceTooLarge', { max: formatMoney(getMaxAmount(tenantCurrency), tenantCurrency, lang) })
        }
        if (errorCode === UNSUPPORTED_CURRENCY_ERROR) return t('productForm.priceCurrencyUnsupported')
        return hasDecimals
            ? t('productForm.priceFormatInvalidDecimal', { currency: tenantCurrency })
            : t('productForm.priceFormatInvalidInteger', { currency: tenantCurrency })
    }

    function validateSalePrice(text) {
        const result = parseMoneyInput(text, tenantCurrency)
        setSalePriceError(result.ok ? null : priceErrorMessage(result.error))
        return result
    }

    function handleSalePriceChange(event) {
        const next = event.target.value
        if (isBlockedDecimalKeystroke(salePrice, next, tenantCurrency)) return
        setSalePriceText(next)
        if (isBulkInsert(salePrice, next)) validateSalePrice(next)
        else setSalePriceError(null)
    }

    function handleSalePriceBlur() {
        if (salePriceText === null) return
        const result = validateSalePrice(salePrice)
        if (result.ok && result.value !== null) setSalePriceText(formatAmountForInput(result.value, tenantCurrency))
    }

    function commitCategoryFromInput() {
        const nextCategory = catSearch.trim()
        setCatSearch(nextCategory)
        setCategory(nextCategory)
        setShowCatDrop(false)
        return nextCategory
    }

    async function handleSave() {
        if (!tenantId) {
            setError(t('common.requestFailed'))
            return
        }
        const resolvedCategory = (category || catSearch).trim()
        if (!name || !resolvedCategory) { setError(t('productForm.nameRequired')); return }
        // Save is disabled while unresolved; the inline hint explains why.
        if (!currencyResolved) return
        // Untouched loaded prices (visible or hidden) are sent exactly as loaded:
        // stored values are not re-validated against the tenant scale or max.
        const untouchedSale = salePriceText === null ? loadedAmountOr(loadedPrices.sale, null) : null
        const saleResult = untouchedSale !== null ? { ok: true, value: untouchedSale } : validateSalePrice(salePrice)
        if (!saleResult.ok) { setError(t('productForm.sellingPriceCheck')); return }
        if (saleResult.value === null) { setError(t('productForm.sellingPriceRequired')); return }
        const parsedSalePrice = saleResult.value
        const parsedBasePrice = loadedAmountOr(loadedPrices.base, parsedSalePrice)
        const parsedOriginalPrice = loadedAmountOr(loadedPrices.original, parsedBasePrice)

        const parsedStockQty = Number(stockQty || 0)
        if (!Number.isInteger(parsedStockQty) || parsedStockQty < 0) { setError(t('productForm.stockQtyInvalid')); return }
        if (parsedStockQty > MAX_STOCK_QTY) { setError(t('productForm.stockQtyMax')); return }
        let parsedMaxQty = null
        if (maxQty) {
            parsedMaxQty = Number(maxQty)
            if (!Number.isInteger(parsedMaxQty) || parsedMaxQty <= 0) { setError(t('productForm.maxQtyInvalid')); return }
            if (parsedMaxQty > MAX_STOCK_QTY) { setError(t('productForm.maxQtyMax')); return }
        }
        if (availableFrom && !isValidTimeValue(availableFrom)) { setError(t('productForm.availableFromInvalid')); return }
        if (availableTo && !isValidTimeValue(availableTo)) { setError(t('productForm.availableToInvalid')); return }
        if (availableFrom && availableTo && timeToMinutes(availableFrom) >= timeToMinutes(availableTo)) {
            setError(t('productForm.availableTimeRangeInvalid'))
            return
        }

        const parseOptionalNonNegativeInt = (value, errorKey) => {
            if (!value && value !== 0) return null
            const normalizedValue = String(value).trim()
            if (!normalizedValue) return null
            const parsed = Number(normalizedValue)
            if (!Number.isInteger(parsed) || parsed < 0) {
                setError(t(errorKey))
                return NaN
            }
            return parsed
        }

        const parsedDuration = parseOptionalNonNegativeInt(duration, 'productForm.durationInvalid')
        if (Number.isNaN(parsedDuration)) return
        const parsedSessionCount = parseOptionalNonNegativeInt(sessionCount, 'productForm.sessionCountInvalid')
        if (Number.isNaN(parsedSessionCount)) return
        const parsedCapacity = parseOptionalNonNegativeInt(capacity, 'productForm.capacityInvalid')
        if (Number.isNaN(parsedCapacity)) return

        let parsedAttributes = {}
        if (attributesInput.trim()) {
            try {
                const candidate = JSON.parse(attributesInput)
                if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
                    setError(t('productForm.attributesInvalid'))
                    return
                }
                parsedAttributes = candidate
            } catch {
                setError(t('productForm.attributesInvalid'))
                return
            }
        }

        setSaving(true)
        setError(null)
        try {
            const parsedTags = parseCsv(tags)
            const parsedKeywords = parseCsv(keywords)
            const parsedBenefits = parseCsv(benefits)
            const parsedTargetAudience = parseCsv(targetAudience)
            const resolvedOfferingType = (offeringType || OFFERING_TYPE_BY_INDUSTRY[tenantIndustry] || 'product').trim()
            const resolvedPricingUnit = (pricingUnit || PRICING_UNIT_BY_INDUSTRY[tenantIndustry] || 'item').trim()
            const normalizedName = name.trim()
            const primaryImageUrl = images[0]?.url || null
            const galleryImageUrls = images.map(image => image?.url).filter(Boolean)
            const payload = {
                label: (label || skuCode || normalizedName).trim(),
                name: normalizedName,
                description: description || shortDesc || detailedDesc || null,
                short_description: shortDesc || null,
                detailed_description: detailedDesc || null,
                offering_type: resolvedOfferingType,
                category: resolvedCategory,
                industry_type: tenantIndustry,
                highlight: highlight || null,
                benefits: parsedBenefits,
                pricing: {
                    base_price: parsedBasePrice,
                    sale_price: parsedSalePrice,
                    // Tenant currency is authoritative; the BE may override it.
                    currency: tenantCurrency,
                    unit: resolvedPricingUnit,
                    original_price: parsedOriginalPrice,
                },
                media: {
                    primary_image_url: primaryImageUrl,
                    gallery_image_urls: galleryImageUrls,
                    video_url: videoUrl || null,
                },
                promotion_content: promotionContent || null,
                stock_quantity: parsedStockQty,
                sku_code: skuCode || null,
                tags: parsedTags,
                specifications: specifications || null,
                link: link || null,
                active,
                available_from: availableFrom || null,
                available_to: availableTo || null,
                max_quantity: parsedMaxQty ?? 0,
                attributes: parsedAttributes,
                keywords: parsedKeywords,
                target_audience: parsedTargetAudience,
                experience_level: experienceLevel || null,
                goal: goal || null,
                duration: parsedDuration ?? null,
                duration_unit: durationUnit || null,
                session_count: parsedSessionCount ?? null,
                schedule_type: scheduleType || null,
                capacity: parsedCapacity ?? null,
                size: size || null,
                location: location || null,
                format: format || null,
            }
            if (isEdit) {
                await updateProduct(tenantId, productId, payload)
            } else {
                await createProduct(tenantId, payload)
            }
            navigate(`${prefix}/products`)
        } catch (e) {
            setError(formatApiError(e, t('common.requestFailed')))
        } finally {
            setSaving(false)
        }
    }

    async function handleImageUpload(e) {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file || !isEdit || !tenantId) return
        if (images.length >= MAX_PRODUCT_IMAGES) {
            setError(t('productForm.imageLimitExceeded', { max: MAX_PRODUCT_IMAGES }))
            return
        }
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
            setError(t('productForm.imageTypeInvalid'))
            return
        }
        if (file.size >= MAX_IMAGE_SIZE_BYTES) {
            setError(t('productForm.imageSizeTooLarge'))
            return
        }
        setError(null)
        setUploading(true)
        try {
            const res = await uploadProductImage(tenantId, productId, file)
            setImages(prev => [...prev, { url: res.url, source: 'upload' }])
        } catch (err) {
            Sentry.captureException(err, {
              tags: { action: 'upload_product_image' },
              extra: { tenantId, productId },
            })
        }
        setUploading(false)
    }

    async function handleImageDelete(url) {
        if (!tenantId) return
        try {
            await deleteProductImage(tenantId, productId, url)
            setImages(prev => prev.filter(i => i.url !== url))
        } catch (err) {
            Sentry.captureException(err, {
              tags: { action: 'delete_product_image' },
              extra: { tenantId, productId },
            })
        }
    }

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>{t('productForm.loading')}</div>

    if (!canManageProducts) {
        const deniedContent = (
            <div style={{ padding: isDesktop ? '24px 32px' : '16px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
                <div style={{ background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 16 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 8 }}>
                        {t('common.unauthorized', { defaultValue: 'You do not have permission to access this feature.' })}
                    </div>
                    <button
                        onClick={() => navigate(`${prefix}/products`)}
                        style={{ padding: '8px 12px', border: '0.5px solid #d1d5db', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151' }}
                    >
                        {t('common.back')}
                    </button>
                </div>
            </div>
        )
        return isDesktop
            ? <DesktopShell>{deniedContent}</DesktopShell>
            : <AppShell activeTab="products">{deniedContent}</AppShell>
    }

    const content = (
        <div style={{ padding: isDesktop ? '24px 32px' : '16px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => navigate(`${prefix}/products`)}
                        style={{ padding: '6px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#666' }}>
                        {t('common.back')}
                    </button>
                    <div style={{ fontSize: isDesktop ? 20 : 16, fontWeight: 700, color: '#111' }}>
                        {isEdit ? t('productForm.editTitle') : t('productForm.addTitle')}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {error && <span style={{ fontSize: 12, color: '#ef4444', alignSelf: 'center' }}>{error}</span>}
                    <button onClick={handleSave} disabled={saving || !currencyResolved}
                        style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: 'var(--gc-primary)', color: 'white', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: saving || !currencyResolved ? 0.7 : 1 }}>
                        {saving ? t('common.saving') : t('common.save')}
                    </button>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 400px' : '1fr', gap: 16 }}>
                <div>
                    <div style={sectionStyle}>
                        <div style={sectionTitle}>{t('productForm.basicInfo')}</div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.productImages')}</label>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                                {images.map((img, i) => (
                                    <div key={i} style={{ position: 'relative', width: 90, height: 90 }}>
                                        <img src={img.url} alt="" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '0.5px solid #e0e3f0' }} />
                                        <button onClick={() => handleImageDelete(img.url)}
                                            style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', border: 'none', color: 'white', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                                    </div>
                                ))}
                                {images.length < MAX_PRODUCT_IMAGES && (
                                    <button onClick={() => isEdit ? fileRef.current?.click() : alert(t('productForm.saveFirstForImages'))}
                                        style={{ width: 90, height: 90, border: '1.5px dashed #c0c9f0', borderRadius: 8, background: '#f8f9ff', cursor: 'pointer', fontSize: 24, color: 'var(--gc-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {uploading ? '...' : '+'}
                                    </button>
                                )}
                            </div>
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>{t('productForm.imageLimitHint', { count: images.length, max: MAX_PRODUCT_IMAGES })}</div>
                            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleImageUpload} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.label')}</label>
                            <input style={inputStyle} value={label} onChange={e => setLabel(e.target.value)} placeholder={t('productForm.labelPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.productName')}</label>
                            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder={t('productForm.productNamePh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.description')}</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder={t('productForm.descriptionPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.shortDesc')}</label>
                            <input style={inputStyle} value={shortDesc} onChange={e => setShortDesc(e.target.value)} placeholder={t('productForm.shortDescPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.detailedDesc')}</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4} value={detailedDesc} onChange={e => setDetailedDesc(e.target.value)} placeholder={t('productForm.detailedDescPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <div>
                                <label style={labelStyle}>{t('productForm.offeringType')}</label>
                                <input style={inputStyle} value={offeringType} onChange={e => setOfferingType(e.target.value)} placeholder={t('productForm.offeringTypePh')} />
                            </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.promotionContent')}</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={promotionContent} onChange={e => setPromotionContent(e.target.value)} placeholder={t('productForm.promotionContentPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.keywordsLabel')}</label>
                            <input style={inputStyle} value={keywords} onChange={e => setKeywords(e.target.value)} placeholder={t('productForm.keywordsPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.specifications')}</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={specifications} onChange={e => setSpecifications(e.target.value)} placeholder={t('productForm.specificationsPh')} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.videoUrl')}</label>
                            <input style={inputStyle} value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder={t('productForm.videoUrlPh')} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('productForm.productLink')}</label>
                            <input style={inputStyle} value={link} onChange={e => setLink(e.target.value)} placeholder={t('productForm.linkPh')} />
                        </div>
                    </div>

                    <div style={sectionStyle}>
                        <div style={sectionTitle}>{t('productForm.priceInfo')}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label htmlFor="product-sale-price" style={labelStyle}>{t('productForm.sellingPrice')}</label>
                                <input
                                    id="product-sale-price"
                                    style={{ ...inputStyle, ...(salePriceError ? { borderColor: '#ef4444' } : null), ...(currencyResolved ? null : { background: '#f8f9ff' }) }}
                                    value={salePrice}
                                    onChange={handleSalePriceChange}
                                    onBlur={handleSalePriceBlur}
                                    placeholder={hasDecimals ? '0.00' : '0'}
                                    inputMode={hasDecimals ? 'decimal' : 'numeric'}
                                    disabled={!currencyResolved}
                                    aria-invalid={Boolean(salePriceError)}
                                    aria-describedby={salePriceError ? 'product-sale-price-error' : undefined}
                                />
                            </div>
                            <div>
                                <label htmlFor="product-currency" style={labelStyle}>{t('productForm.currency')}</label>
                                <input id="product-currency" style={{ ...inputStyle, background: '#f8f9ff', color: '#666' }} value={currencyResolved ? tenantCurrency : ''} readOnly />
                            </div>
                        </div>
                        {saleHasExtraPrecision && !salePriceError && (
                            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>{t('productForm.priceExtraPrecisionHint', { currency: tenantCurrency })}</div>
                        )}
                        {salePriceError && (
                            <div id="product-sale-price-error" role="alert" style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>{salePriceError}</div>
                        )}
                        {!currencyResolved && (
                            <div style={{ fontSize: 12, color: currencyError ? '#ef4444' : '#666', marginTop: 6 }}>
                                {t(currencyError ? 'productForm.currencyLoadFailed' : 'productForm.currencyLoading')}
                            </div>
                        )}
                    </div>

                    <div style={sectionStyle}>
                        <div style={sectionTitle}>{t('productForm.additionalInfo')}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label style={labelStyle}>{t('productForm.stockQty')}</label>
                                <input
                                    style={inputStyle}
                                    value={stockQty}
                                    onChange={e => {
                                        const digits = sanitizeDigits(e.target.value)
                                        if (!digits) {
                                            setStockQty('')
                                            return
                                        }
                                        const parsed = Number(digits)
                                        if (!Number.isFinite(parsed)) return
                                        setStockQty(String(Math.min(parsed, MAX_STOCK_QTY)))
                                    }}
                                    placeholder="0"
                                    inputMode="numeric"
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>{t('productForm.maxQty')}</label>
                                <input
                                    style={inputStyle}
                                    value={maxQty}
                                    onChange={e => {
                                        const digits = sanitizeDigits(e.target.value)
                                        if (!digits) {
                                            setMaxQty('')
                                            return
                                        }
                                        const parsed = Number(digits)
                                        if (!Number.isFinite(parsed)) return
                                        setMaxQty(String(Math.min(parsed, MAX_STOCK_QTY)))
                                    }}
                                    placeholder={t('productForm.maxQtyPh')}
                                    inputMode="numeric"
                                />
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label style={labelStyle}>{t('productForm.availableFrom')}</label>
                                <input style={inputStyle} type="time" value={availableFrom} onChange={e => setAvailableFrom(e.target.value)} placeholder="09:00" />
                            </div>
                            <div>
                                <label style={labelStyle}>{t('productForm.availableTo')}</label>
                                <input style={inputStyle} type="time" value={availableTo} onChange={e => setAvailableTo(e.target.value)} placeholder="22:00" />
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <div style={sectionStyle}>
                        <div style={sectionTitle}>{t('productForm.skuTags')}</div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>{t('productForm.skuCode')}</label>
                            <input style={inputStyle} value={skuCode} onChange={e => setSkuCode(e.target.value)} placeholder={t('productForm.skuPh')} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div onClick={() => setActive(a => !a)}
                                style={{ width: 40, height: 22, borderRadius: 11, background: active ? 'var(--gc-primary)' : '#e0e3f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                                <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: active ? 20 : 2, transition: 'left 0.2s' }} />
                            </div>
                            <span style={{ fontSize: 13, color: active ? '#16a34a' : '#999' }}>{active ? t('common.active') : t('common.inactive')}</span>
                        </div>
                    </div>

                    <div style={sectionStyle}>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(prev => !prev)}
                            style={{
                                width: '100%',
                                border: '0.5px solid #e0e3f0',
                                borderRadius: 8,
                                background: '#f8f9ff',
                                padding: '10px 12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#333',
                                marginBottom: showAdvanced ? 12 : 0,
                            }}
                        >
                            <span>{t('productForm.advancedSection')}</span>
                            <span>{showAdvanced ? '▲' : '▼'}</span>
                        </button>
                        {showAdvanced && (
                            <div>
                                <div ref={catRef} style={{ position: 'relative', marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.category')}</label>
                                    <input style={inputStyle} value={catSearch}
                                        onChange={e => { setCatSearch(e.target.value); setCategory(e.target.value); setShowCatDrop(true) }}
                                        onFocus={e => { e.target.select(); setShowCatDrop(true) }}
                                        onBlur={commitCategoryFromInput}
                                        placeholder={t('productForm.categorySearchPh')} />
                                    {showCatDrop && filteredCats.length > 0 && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                            {filteredCats.map(c => (
                                                <div
                                                    key={c}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => { const v = c.replace(` ${t('productForm.categoryNew')}`, '').trim(); setCategory(v); setCatSearch(v); setShowCatDrop(false) }}
                                                    style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', color: c.includes(t('productForm.categoryNew')) ? 'var(--gc-primary)' : '#333' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gc-page-bg)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                                >
                                                    {c.replace(/_/g, ' ')}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.tags')}</label>
                                    <input style={inputStyle} value={tags} onChange={e => setTags(e.target.value)} placeholder={t('productForm.tagsPh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.highlight')}</label>
                                    <input style={inputStyle} value={highlight} onChange={e => setHighlight(e.target.value)} placeholder={t('productForm.highlightPh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.benefits')}</label>
                                    <input style={inputStyle} value={benefits} onChange={e => setBenefits(e.target.value)} placeholder={t('productForm.benefitsPh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.targetAudience')}</label>
                                    <input style={inputStyle} value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder={t('productForm.targetAudiencePh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.experienceLevel')}</label>
                                    <input style={inputStyle} value={experienceLevel} onChange={e => setExperienceLevel(e.target.value)} placeholder={t('productForm.experienceLevelPh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.goal')}</label>
                                    <input style={inputStyle} value={goal} onChange={e => setGoal(e.target.value)} placeholder={t('productForm.goalPh')} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                    <div>
                                        <label style={labelStyle}>{t('productForm.duration')}</label>
                                        <input style={inputStyle} value={duration} onChange={e => setDuration(sanitizeDigits(e.target.value))} placeholder="0" inputMode="numeric" />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{t('productForm.durationUnit')}</label>
                                        <input style={inputStyle} value={durationUnit} onChange={e => setDurationUnit(e.target.value)} placeholder={t('productForm.durationUnitPh')} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                    <div>
                                        <label style={labelStyle}>{t('productForm.sessionCount')}</label>
                                        <input style={inputStyle} value={sessionCount} onChange={e => setSessionCount(sanitizeDigits(e.target.value))} placeholder="0" inputMode="numeric" />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{t('productForm.capacity')}</label>
                                        <input style={inputStyle} value={capacity} onChange={e => setCapacity(sanitizeDigits(e.target.value))} placeholder="0" inputMode="numeric" />
                                    </div>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.scheduleType')}</label>
                                    <input style={inputStyle} value={scheduleType} onChange={e => setScheduleType(e.target.value)} placeholder={t('productForm.scheduleTypePh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.size')}</label>
                                    <input style={inputStyle} value={size} onChange={e => setSize(e.target.value)} placeholder={t('productForm.sizePh')} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>{t('productForm.location')}</label>
                                    <input style={inputStyle} value={location} onChange={e => setLocation(e.target.value)} placeholder={t('productForm.locationPh')} />
                                </div>
                                <div>
                                    <label style={labelStyle}>{t('productForm.format')}</label>
                                    <input style={inputStyle} value={format} onChange={e => setFormat(e.target.value)} placeholder={t('productForm.formatPh')} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )

    return isDesktop
        ? <DesktopShell>{content}</DesktopShell>
        : <AppShell activeTab="products">{content}</AppShell>
}
